import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// Remembers referral/UTM details from an offer link (?ref=CODE&utm_source=…) for that offer's checkout.
export interface CampaignAttribution {
  campaign: string;
  referralCode?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  landingUrl: string;
  firstSeenAt: string;
}

const storageKey = (slug: string) => `boqbox_offer_attribution_${slug}`;
const REFERRAL_CODE_PATTERN = /^[A-Z0-9_-]{4,24}$/;

const normalizeReferralCode = (rawCode: string | null): string | undefined => {
  if (!rawCode) return undefined;
  const normalized = rawCode.trim().toUpperCase();
  return REFERRAL_CODE_PATTERN.test(normalized) ? normalized : undefined;
};

export const getStoredCampaignAttribution = (slug: string): CampaignAttribution | undefined => {
  try {
    const stored = localStorage.getItem(storageKey(slug));
    return stored ? (JSON.parse(stored) as CampaignAttribution) : undefined;
  } catch {
    return undefined;
  }
};

export const useCampaignAttribution = (slug: string | undefined): CampaignAttribution | undefined => {
  const location = useLocation();

  const attribution = useMemo(() => {
    if (!slug) return undefined;
    const params = new URLSearchParams(location.search);
    const referralCode = normalizeReferralCode(params.get('ref') || params.get('r'));
    const utmSource = params.get('utm_source') || undefined;
    const utmMedium = params.get('utm_medium') || undefined;
    const utmCampaign = params.get('utm_campaign') || undefined;
    const stored = getStoredCampaignAttribution(slug);

    if (!referralCode && !utmSource && !utmMedium && !utmCampaign) {
      return stored;
    }

    return {
      campaign: slug,
      referralCode,
      utmSource,
      utmMedium,
      utmCampaign,
      landingUrl: window.location.href,
      firstSeenAt: stored?.firstSeenAt || new Date().toISOString(),
    };
  }, [location.search, slug]);

  useEffect(() => {
    if (!attribution || !slug) return;
    try {
      localStorage.setItem(storageKey(slug), JSON.stringify(attribution));
    } catch {
      // Attribution should never block checkout.
    }
  }, [attribution, slug]);

  return attribution;
};
